import { useState } from 'react';
import axios from 'axios';
import { Icon } from '../../components/Icon.tsx';
import { appsApi, type AppKey, type AppStatus } from '../apps/api.ts';
import { titleCase } from './hooks.ts';

// Each app's real logo, copied from that app's own public/icons/<app>-mark.svg
// so the launcher shows what the app actually ships. The PNGs these replaced
// were captured 2026-08-13 and had drifted: WapPro has since moved from a
// clipboard to its tag mark, WapStat from a bar-chart to the house mark, and
// WapMeet's calendar gained its check. Re-copy the source SVG when an app's
// mark changes — nothing syncs these automatically.
//
// wapdocs deliberately still points at the PNG: WapDocs is a WapMeet clone and
// its own icons/ dir still holds WapMeet's calendar (and its icon.svg is a
// stale "WS" placeholder), so copying from source would regress this tile.
// It needs a mark of its own in the house style.
const LOGOS: Record<AppKey, string> = {
  wapmeet: '/icons/apps/wapmeet-mark.svg',
  wapsign: '/icons/apps/wapsign-mark.svg',
  wapstat: '/icons/apps/wapstat-mark.svg',
  wappro: '/icons/apps/wappro-mark.svg',
  wapdocs: '/icons/apps/wapdocs-white.png',
};

type ChipTone = 'emerald' | 'amber' | 'muted';

// Status is conveyed by BOTH the chip's tonal dot and a text label (never
// colour alone) — M3 chips carry the dot themselves via `.pill::before`.
function StatusChip({ tone, label }: { tone: ChipTone; label: string }): JSX.Element {
  const cls =
    tone === 'emerald' ? 'pill-success' : tone === 'amber' ? 'pill-pending' : 'pill-neutral';
  return <span className={`pill ${cls}`}>{label}</span>;
}

export function AppCard({ app }: { app: AppStatus }): JSX.Element {
  const logo = LOGOS[app.key];
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // hasAccount → can launch; statusUnavailable → still let them try (the app may
  // have just come back). Otherwise the card is inert with a one-line reason.
  const canOpen = app.hasAccount || app.statusUnavailable;

  const chip: { tone: ChipTone; label: string } = app.hasAccount
    ? { tone: 'emerald', label: app.role ? titleCase(app.role) : 'Member' }
    : app.statusUnavailable
      ? { tone: 'amber', label: 'Status unavailable' }
      : app.enabled
        ? { tone: 'amber', label: 'No account yet' }
        : { tone: 'muted', label: 'Not enabled' };

  const hint = app.statusUnavailable
    ? `Couldn't reach ${app.name} — try again shortly.`
    : app.enabled
      ? `Ask an admin to add you in ${app.name}.`
      : 'Not enabled for your council.';

  async function open(): Promise<void> {
    setError(null);
    setPending(true);
    try {
      const { url } = await appsApi.open(app.key, app.tile);
      window.location.assign(url);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        const code = (e.response.data as { error?: string } | undefined)?.error;
        setError(
          code === 'app_account_not_found'
            ? `No linked account in ${app.name} yet (emails must match).`
            : 'This app is not available.',
        );
      } else if (axios.isAxiosError(e) && e.response?.status === 502) {
        setError(`${app.name} is unreachable right now. Try again shortly.`);
      } else if (axios.isAxiosError(e) && e.response?.status === 403) {
        setError(`Your council's plan is blocked — opening ${app.name} is disabled.`);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setPending(false);
    }
  }

  return (
    <div className="card-paper p-5 flex min-h-[192px] flex-col">
      <div className="flex items-start justify-between gap-3">
        <img src={logo} alt="" aria-hidden="true" className="size-11 rounded-xl object-cover" />
        <StatusChip tone={chip.tone} label={chip.label} />
      </div>

      <div className="mt-4 flex-1">
        <h3 className="text-xl font-medium text-ink leading-tight">{app.name}</h3>
        <p className="text-sm text-muted-foreground mt-1">{app.tagline}</p>
      </div>

      <div className="mt-4">
        {canOpen ? (
          <button
            type="button"
            onClick={() => void open()}
            disabled={pending}
            className="btn-primary"
            aria-label={`Open ${app.name}`}
          >
            {pending ? (
              <>
                <Icon name="progress_activity" size={18} className="animate-spin" /> Opening…
              </>
            ) : (
              <>
                Open <Icon name="arrow_outward" size={18} />
              </>
            )}
          </button>
        ) : (
          <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>
        )}
        {error && (
          <p role="alert" className="mt-2 text-xs text-destructive leading-relaxed">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
